import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GoogleMap, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import MarkerClusterer from 'marker-clusterer-plus';
import { OverlappingMarkerSpiderfier } from 'ts-overlapping-marker-spiderfier';
import { useMediaQuery } from 'react-responsive';
import { useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { getElementAttribute, getMapPreviewSrc, getReactElement, isMapBootstrapReady } from '../utils/element';
import MapContent from './MapContent';
import StaticMapPreview from './StaticMapPreview';
import { useGetStoresQuery } from '../store/services/stores';
import { MediaQueries } from '../types/media-queries';
import { GMAP_STYLES } from '../constants/gmap-styles';
import { getFloatingMarkerStyle, offsetLatLng, rhumbBearingByPoints } from '../utils/map';
import { selectStore as selectStoreReducer, resetSelectedStore as resetSelectedStoreReducer, getSelectedStore, } from '../store/slices/storesSlice';
import { getPosition, getRegion } from '../store/slices/locationSlice';
import { StoreCategory } from '../types/store';
import { getMapContentCollapsed, getShowClosestStoreMarker, openMapContent as openMapContentReducer, } from '../store/slices/uiSlice';
import { MIN_DISTANCE_TO_STORE } from '../constants/stores';
import { REGIONS } from '../constants/regions';
import { FloatingMarkerPosition } from '../types/map';
import { getClosestIqosStore, getFilteredStores } from '../store/selectors/storesSelectors';
import StoreDetails from './StoreDetails';
import { usePrevious } from '../hooks/state';
import { basePath, routeConfigByUrl } from '../../../utils/router';
import { useCurrentPath } from '../hooks/router';
import { RouteName } from '../router/route-names';
const containerStyle = {
    width: '100%',
    height: '100%',
};
const isAgeGateBlocking = () => {
    return document.body.classList.contains('modal-open') && Boolean(document.querySelector('.js__age-gate-modal'));
};
const canBootstrapMap = () => {
    return isMapBootstrapReady() || !isAgeGateBlocking();
};
const markMapBootstrapReady = () => {
    getReactElement().dataset.mapBootstrapReady = '1';
};
const InteractiveMap = () => {
    const zoom = 7;
    const maxClusterZoom = 16;
    const mapOptions = {
        disableDefaultUI: true,
        clickableIcons: false,
        maxZoom: maxClusterZoom + 2,
        styles: GMAP_STYLES,
    };
    const infoWindowOptions = {
        pixelOffset: {
            width: -200,
            height: -55,
            equals: () => true,
        },
    };
    const { t } = useTranslation();
    const location = useLocation();
    const currentPath = useCurrentPath();
    const isDesktopScreen = useMediaQuery({
        query: `(min-width: ${MediaQueries.SM}px)`,
    });
    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: getElementAttribute('data-google-maps-key'),
    });
    const { data: stores } = useGetStoresQuery('');
    const [isMapLoaded, setIsMapLoaded] = useState(false);
    const [isMapClustered, setIsMapClustered] = useState(false);
    const [floatingMarkerStyle, setFloatingMarkerStyle] = useState({});
    const [isClosestStoreNotInBounds, setIsClosestStoreNotInBounds] = useState(false);
    const [center, setCenter] = useState({ lat: 58.998056, lng: 25.026667 });
    const [isInfoWindowOpen, setIsInfoWindowOpen] = useState(false);
    const [infoWindowPosition, setInfoWindowPosition] = useState(null);
    const [clusterer, setClusterer] = useState(null);
    const [spiderfier, setSpiderfier] = useState(null);
    const [markers, setMarkers] = useState([]);
    const [map, setMap] = React.useState(null);
    const [isMapContentAnimationVisible, setIsMapContentAnimationVisible] = React.useState(true);
    const position = useSelector(getPosition);
    const region = useSelector(getRegion);
    const closestIqosStore = useSelector(getClosestIqosStore);
    const showClosestStoreMarker = useSelector(getShowClosestStoreMarker);
    const mapContentCollapsed = useSelector(getMapContentCollapsed);
    const selectedStore = useSelector(getSelectedStore);
    const filteredStores = useSelector(getFilteredStores);
    const dispatch = useDispatch();
    const mapPreviewSrc = getMapPreviewSrc();
    const mapRef = useRef(null);
    const mapBackButtonRef = useRef(null);
    const mapContainerRef = useRef(null);
    const closestIqosStoreDistance = useMemo(() => {
        return closestIqosStore && closestIqosStore.distanceToPosition
            ? `${Math.round(closestIqosStore.distanceToPosition)} km`
            : '-';
    }, [closestIqosStore]);
    const fitBoundsPadding = useMemo(() => {
        return isDesktopScreen ? { top: 0, right: 0, left: 400, bottom: 0 } : undefined;
    }, [isDesktopScreen]);
    const resetSelectedStore = () => {
        dispatch(resetSelectedStoreReducer());
    };
    const closeInfoWindow = () => {
        resetSelectedStore();
        setIsInfoWindowOpen(false);
    };
    const getStoreMarker = (store) => {
        const index = filteredStores.map(e => e.id).indexOf(store.id);
        return markers[index];
    };
    const getMarkerStore = (marker) => {
        return filteredStores[markers.indexOf(marker)];
    };
    const getStoreMarkerIcon = (store) => {
        const storeCategory = StoreCategory;
        return {
            // eslint-disable-next-line import/no-dynamic-require
            url: require(`../../../../img/store-locator/icon-marker-${store.reseller ? storeCategory.Iqos : store.category}.svg`),
            size: { width: 40, height: 60 },
        };
    };
    const selectStore = (store) => {
        dispatch(selectStoreReducer(store));
        window.dataLayer.push({
            event: 'storeLocatorStoreClick',
            storeLocatorStoreID: store.id,
            storeLocatorStoreName: store.name,
            storeLocatorStoreType: store.category,
            storeLocatorStoreClickSource: 'Map',
        });
    };
    const removeSpiderfierListeners = () => {
        spiderfier?.removeListener('unspiderfy', () => null);
        spiderfier?.removeListener('format', () => null);
    };
    const addSpiderfierListeners = (spiderfierObject) => {
        const localSpiderfier = spiderfier || spiderfierObject;
        localSpiderfier.addListener('unspiderfy', () => {
            if (selectedStore && markers.indexOf(getStoreMarker(selectedStore)) !== -1) {
                // Resets selected store if selected marker gets unspiderfied
                resetSelectedStore();
            }
        });
        localSpiderfier.addListener('format', (marker, status) => {
            const zoom = map?.getZoom();
            if (zoom && zoom > maxClusterZoom) {
                if (status === OverlappingMarkerSpiderfier.markerStatus.SPIDERFIABLE) {
                    // Makes spiderfiable marker to look like a cluster
                    marker.setIcon({
                        url: require('../../../../img/store-locator/icon-cluster.svg'),
                        size: { width: 32, height: 32 },
                    });
                    marker.setLabel({
                        text: `${spiderfierObject.markersNearMarker(marker).length + 1}`,
                        color: '#fffdfb',
                        fontSize: '16px',
                        fontFamily: 'IQOS',
                        fontWeight: 'bold',
                    });
                }
                else {
                    const store = getMarkerStore(marker);
                    if (store) {
                        // Resets marker icon
                        marker.setIcon(getStoreMarkerIcon(store));
                        marker.setLabel(null);
                    }
                }
            }
        });
    };
    const initSpiderfier = (map) => {
        if (!spiderfier) {
            const spiderfierObject = new OverlappingMarkerSpiderfier(map, {
                markersWontMove: true,
                markersWontHide: true,
                basicFormatEvents: false,
                keepSpiderfied: true,
                ignoreMapClick: true,
                circleSpiralSwitchover: 7,
                spiralLengthStart: 50,
                circleFootSeparation: 50,
                spiralFootSeparation: 50,
            });
            spiderfierObject.legColors.usual[google.maps.MapTypeId.ROADMAP] = '#34303d';
            spiderfierObject.legColors.highlighted[google.maps.MapTypeId.ROADMAP] = '#00d1d2';
            addSpiderfierListeners(spiderfierObject);
            setSpiderfier(spiderfierObject);
        }
    };
    useEffect(() => {
        if (spiderfier && markers.length) {
            removeSpiderfierListeners();
            addSpiderfierListeners(spiderfier);
        }
    }, [markers]);
    const initClusterer = (map) => {
        if (!clusterer) {
            const clustererObject = new MarkerClusterer(map, markers, {
                maxZoom: maxClusterZoom,
                zoomOnClick: false,
                averageCenter: true,
                styles: [
                    {
                        url: require('../../../../img/store-locator/icon-cluster.svg'),
                        width: 32,
                        height: 32,
                        textColor: '#fffdfb',
                        textSize: 16,
                        fontFamily: 'IQOS',
                    },
                ],
            });
            setClusterer(clustererObject);
            google.maps.event.addListener(clustererObject, 'clusteringbegin', () => {
                setIsMapClustered(false);
            });
            google.maps.event.addListener(clustererObject, 'clusteringend', () => {
                setIsMapClustered(true);
            });
            google.maps.event.addListener(clustererObject, 'click', (cluster) => {
                const markers = cluster.getMarkers();
                // Zooms in to expand cluster
                map.fitBounds(cluster.getBounds(), fitBoundsPadding);
                google.maps.event.addListenerOnce(map, 'idle', () => {
                    const zoom = map.getZoom();
                    if (zoom &&
                        zoom > maxClusterZoom &&
                        spiderfier?.markersNearMarker(markers[0]).length === markers.length - 1) {
                        resetSelectedStore();
                    }
                });
                window.dataLayer.push({
                    event: 'storeLocatorClusterClick',
                });
            });
        }
    };
    const openMapContent = () => {
        dispatch(openMapContentReducer());
        window.dataLayer.push({
            event: 'storeLocatorBackToFilterClick',
        });
    };
    const drawMarkers = async () => {
        spiderfier?.forgetAllMarkers();
        const newMarkers = filteredStores.map(store => {
            const marker = new google.maps.Marker({
                map,
                clickable: true,
                draggable: false,
                position: { lat: store.latitude, lng: store.longitude },
                icon: getStoreMarkerIcon(store),
            });
            spiderfier?.trackMarker(marker, (event) => {
                if (selectedStore !== store) {
                    selectStore(store);
                }
                if (!isDesktopScreen && (selectedStore !== store || event)) {
                    openMapContent();
                }
            });
            return marker;
        });
        setMarkers(newMarkers);
        clusterer?.clearMarkers();
        clusterer?.addMarkers(newMarkers, true);
        clusterer?.repaint();
    };
    const openInfoWindow = (marker) => {
        setInfoWindowPosition(marker.getPosition());
        setIsInfoWindowOpen(true);
    };
    const centerAndZoomTo = async (latLng, zoom = 15) => {
        let center = latLng;
        if (isDesktopScreen) {
            center = await offsetLatLng(map, center);
        }
        return new Promise(resolve => {
            if (map.getZoom() !== zoom) {
                google.maps.event.addListenerOnce(map, 'idle', () => {
                    resolve(null);
                });
                map.setZoom(zoom);
                map.setCenter(center);
            }
            else {
                map.setCenter(center);
                resolve(null);
            }
        });
    };
    const centerToPosition = (position, track = true) => {
        centerAndZoomTo({ lat: position.coords.latitude, lng: position.coords.longitude });
        if (track) {
            window.dataLayer.push({
                event: 'storeLocatorCenterToLocationClick',
            });
        }
    };
    const setClosestStoreMarkerPositionStyle = (bearing) => {
        const mapBordersOffset = 46;
        const mapHeight = mapContainerRef.current.offsetHeight || 0;
        const mapWidth = mapContainerRef.current.offsetWidth || 0;
        const visualSideContentWidth = isDesktopScreen ? 400 : 0;
        const sideContentWidth = isDesktopScreen ? 400 : mapBackButtonRef.current.clientWidth + 15;
        const sideContentHeight = isDesktopScreen
            ? mapContainerRef.current.clientHeight
            : mapBackButtonRef.current.clientHeight + 15;
        const visualSideContentHeight = isDesktopScreen ? 0 : sideContentHeight;
        const mapVisualWidth = mapWidth - visualSideContentWidth;
        const topAngle = (180 * mapVisualWidth) / (mapVisualWidth + mapHeight);
        const sideAngle = 180 - topAngle;
        const heightStep = (mapHeight - mapBordersOffset - visualSideContentHeight) / sideAngle;
        const widthStep = (mapWidth - mapBordersOffset - visualSideContentWidth) / topAngle;
        let position;
        if (Math.abs(bearing) <= topAngle / 2) {
            position = FloatingMarkerPosition.Top;
        }
        else if (bearing > topAngle / 2 && bearing <= 180 - topAngle / 2) {
            position = FloatingMarkerPosition.Right;
        }
        else if (bearing < -topAngle / 2 && bearing > -(180 - topAngle / 2)) {
            position = FloatingMarkerPosition.Left;
        }
        else {
            position = FloatingMarkerPosition.Bottom;
        }
        setFloatingMarkerStyle(getFloatingMarkerStyle(position, widthStep, heightStep, bearing, sideContentWidth, topAngle, visualSideContentWidth, visualSideContentHeight, sideContentHeight));
    };
    const isMarkerClustered = (marker) => {
        if (!clusterer) {
            return false;
        }
        return clusterer
            .getClusters()
            .some(cluster => cluster.getSize() > 1 && cluster.getMarkers().indexOf(marker) !== -1);
    };
    const centerChanged = async () => {
        if (showClosestStoreMarker &&
            closestIqosStore &&
            closestIqosStore.distanceToPosition &&
            closestIqosStore.distanceToPosition > MIN_DISTANCE_TO_STORE &&
            map) {
            const sideContentWidth = isDesktopScreen ? 400 : 0;
            const center = map.getCenter();
            const bounds = map.getBounds();
            const storeOffsetBySideContent = await offsetLatLng(map, {
                lat: closestIqosStore.latitude,
                lng: closestIqosStore.longitude,
            }, sideContentWidth);
            const centerOffsetBySideContent = await offsetLatLng(map, {
                lat: center?.lat() || 0,
                lng: center?.lng() || 0,
            }, -sideContentWidth / 2);
            const closestIqosStorePosition = {
                lat: closestIqosStore.latitude,
                lng: closestIqosStore.longitude,
            };
            const isClosestStoreInBounds = bounds && bounds.contains(closestIqosStorePosition);
            const isOffsettedClosestStoreInBounds = bounds && bounds.contains(storeOffsetBySideContent);
            setIsClosestStoreNotInBounds(!isClosestStoreInBounds || !isOffsettedClosestStoreInBounds);
            const rhumbLine = rhumbBearingByPoints(centerOffsetBySideContent, closestIqosStorePosition);
            setClosestStoreMarkerPositionStyle(rhumbLine);
        }
    };
    const addAltTexts = () => {
        const altTextsInterval = setInterval(() => {
            const $map = $('.map-container .gm-style');
            const $clusters = $map.find('.cluster');
            const $pinImages = $map.find('div:not(.cluster) > img:not([role="presentation"])');
            if ($clusters.length && $map.find(`[aria-label="${t('store_locator.map.pin.label')}"]`).length) {
                clearInterval(altTextsInterval);
            }
            $clusters.each((index, element) => {
                const $cluster = $(element);
                if ($cluster.html()) {
                    $cluster.css('background-color', '#34303d').css('border-radius', '32px');
                    $cluster.find('img').attr('alt', t('store_locator.map.cluster.image_alt'));
                }
            });
            $pinImages.each((index, element) => {
                const $pinImage = $(element);
                $pinImage.attr('alt', t('store_locator.map.pin.image_alt'));
                $pinImage.parent().attr('aria-label', t('store_locator.map.pin.label'));
            });
        }, 500);
    };
    useEffect(() => {
        addAltTexts();
    }, [map, isLoaded, isMapLoaded, spiderfier, clusterer, isMapClustered, JSON.stringify(filteredStores), position]);
    useEffect(() => {
        if (map && !spiderfier && isLoaded && isMapLoaded) {
            initSpiderfier(map);
        }
        if (map && !clusterer && isLoaded && isMapLoaded && spiderfier) {
            initClusterer(map);
        }
    }, [map, isLoaded, isMapLoaded, spiderfier, clusterer, isMapClustered]);
    useEffect(() => {
        if (isLoaded && isMapLoaded && clusterer && spiderfier && filteredStores.length) {
            drawMarkers();
        }
    }, [JSON.stringify(filteredStores), isLoaded, isMapLoaded, spiderfier, clusterer]);
    useEffect(() => {
        const bounds = region && REGIONS.find(r => r.key === region).bounds;
        if (bounds) {
            map.fitBounds(bounds, fitBoundsPadding);
        }
    }, [region]);
    useEffect(() => {
        if (position && map) {
            centerToPosition(position, false);
        }
    }, [position, map]);
    useEffect(() => {
        centerChanged();
    }, [closestIqosStore]);
    const oldSelectedStore = usePrevious(selectedStore);
    const onSelectedStoreChanged = useCallback(async (selectedStore, oldSelectedStore) => {
        if (!map) {
            return;
        }
        if (!isMapClustered && clusterer) {
            // Waits for map to load and calls itself again
            google.maps.event.addListenerOnce(clusterer, 'clusteringend', () => {
                onSelectedStoreChanged(selectedStore, oldSelectedStore);
            });
            return;
        }
        if (oldSelectedStore) {
            const marker = getStoreMarker(oldSelectedStore);
            if (marker) {
                const zoom = map.getZoom();
                // Resets old marker if it wasn't spiderfied, otherwise spiderfier format function will take care of it
                if ((zoom && zoom <= maxClusterZoom) || !spiderfier?.markersNearMarker(marker, true).length) {
                    marker.setIcon(getStoreMarkerIcon(oldSelectedStore));
                }
            }
        }
        if (selectedStore) {
            // Updates current marker
            const marker = getStoreMarker(selectedStore);
            if (marker) {
                marker.setIcon(getStoreMarkerIcon(selectedStore));
                const zoom = map.getZoom();
                if ((zoom && zoom <= maxClusterZoom && isMarkerClustered(marker)) ||
                    !map.getBounds()?.contains(marker.getPosition())) {
                    await centerAndZoomTo({ lat: selectedStore.latitude, lng: selectedStore.longitude }, maxClusterZoom + 1);
                }
                openInfoWindow(marker);
            }
        }
    }, [selectedStore, markers]);
    useEffect(() => {
        onSelectedStoreChanged(selectedStore, oldSelectedStore);
    }, [selectedStore, markers]);
    const selectClosestIqosStore = () => {
        selectStore(closestIqosStore);
        window.dataLayer.push({
            event: 'storeLocatorClosestIqosStoreClick',
        });
    };
    const zoomChanged = async () => {
        if (selectedStore) {
            const marker = getStoreMarker(selectedStore);
            if (marker) {
                google.maps.event.addListenerOnce(map, 'idle', () => {
                    // Deselects marker if it gets clustered
                    const mapZoom = map.getZoom() || zoom;
                    if (mapZoom <= maxClusterZoom && isMarkerClustered(marker)) {
                        resetSelectedStore();
                    }
                });
            }
        }
        if (map) {
            addAltTexts();
        }
    };
    const keydownHandler = (event) => {
        if (isDesktopScreen && isInfoWindowOpen && event.keyCode === 27) {
            closeInfoWindow();
        }
    };
    const onLoad = React.useCallback(async (map) => {
        google.maps.event.addListenerOnce(map, 'idle', () => {
            setIsMapLoaded(true);
        });
        if (isDesktopScreen && map) {
            // Offsets map center to compensate map content width
            setCenter(await offsetLatLng(map, center, 200));
        }
        setMap(map);
    }, []);
    const onUnmount = React.useCallback(() => {
        setMap(null);
        document.removeEventListener('keydown', keydownHandler);
    }, []);
    useEffect(() => {
        const routeConfig = routeConfigByUrl(currentPath);
        if (routeConfig && routeConfig.name) {
            setIsMapContentAnimationVisible(![RouteName.SubmitQuestion].includes(routeConfig.name));
        }
        ReactGA.send({
            hitType: 'pageview',
            page_path: basePath(getReactElement()) + currentPath,
            page_location: window.location.href,
            send_page_view: true,
        });
    }, [location]);
    return (_jsxs("div", { className: "map-container", ref: mapContainerRef, children: [mapPreviewSrc && (!isLoaded || !isMapLoaded) && (_jsx(StaticMapPreview, { alt: t('store_locator.main.meta.title'), className: "map-static-preview map-static-preview-overlay", decorative: true })), isLoaded && (_jsxs(_Fragment, { children: [_jsx(GoogleMap, { ref: mapRef, mapContainerStyle: containerStyle, center: center, zoom: zoom, onLoad: onLoad, onUnmount: onUnmount, onCenterChanged: centerChanged, onZoomChanged: zoomChanged, options: mapOptions, children: selectedStore && isDesktopScreen && isInfoWindowOpen && (_jsx(InfoWindow, { options: infoWindowOptions, position: infoWindowPosition, onCloseClick: closeInfoWindow, children: _jsx(StoreDetails, { store: selectedStore }) })) }), _jsx("div", { className: "map-top-left", ref: mapBackButtonRef, children: _jsx("button", { className: "d-md-none btn btn-dark", type: "button", onClick: openMapContent, children: t('store_locator.map.back_to_filter') }) }), _jsx("div", { className: "map-bottom-left", children: position && (_jsx("button", { "aria-label": t('store_locator.map.center_to_position'), className: "btn btn-dark btn-circular", type: "button", onClick: () => {
                                centerToPosition(position);
                            }, children: _jsx("i", { className: "nbw-icon nbw-icon-location-arrow" }) })) }), _jsx(motion.div, { className: "map-content", animate: {
                            ...(isMapContentAnimationVisible && { x: mapContentCollapsed ? '-100%' : '0' }),
                            transition: { ease: [0.43, 0.13, 0.23, 0.96], duration: 0.5 },
                        }, children: _jsx(MapContent, {}) }), showClosestStoreMarker && isClosestStoreNotInBounds && (_jsx("div", { className: "map-floating-marker", style: floatingMarkerStyle, onClick: selectClosestIqosStore, children: closestIqosStoreDistance }))] }))] }));
};
const Map = () => {
    const { t } = useTranslation();
    const [shouldBootstrapMap, setShouldBootstrapMap] = useState(false);
    useEffect(() => {
        let firstFrame = null;
        let secondFrame = null;
        let observer = null;
        if (!shouldBootstrapMap) {
            const scheduleBootstrap = () => {
                if (firstFrame !== null || secondFrame !== null) {
                    return;
                }
                firstFrame = requestAnimationFrame(() => {
                    secondFrame = requestAnimationFrame(() => {
                        markMapBootstrapReady();
                        setShouldBootstrapMap(true);
                    });
                });
            };
            const resolveBootstrapState = () => {
                if (canBootstrapMap()) {
                    scheduleBootstrap();
                }
            };
            resolveBootstrapState();
            observer = new MutationObserver(resolveBootstrapState);
            observer.observe(document.body, {
                attributes: true,
                attributeFilter: ['class'],
                childList: true,
                subtree: true,
            });
        }
        return () => {
            if (observer) {
                observer.disconnect();
            }
            if (firstFrame !== null) {
                cancelAnimationFrame(firstFrame);
            }
            if (secondFrame !== null) {
                cancelAnimationFrame(secondFrame);
            }
        };
    }, [shouldBootstrapMap]);
    if (!shouldBootstrapMap) {
        return (_jsx("div", { className: "map-container map-container-static-preview", children: _jsx(StaticMapPreview, { alt: t('store_locator.main.meta.title') }) }));
    }
    return _jsx(InteractiveMap, {});
};
export default React.memo(Map);
